import { config } from './config';
import { getAuthHeaders } from './session';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: Array<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const message = data?.error ?? `Erreur ${res.status}`;
    throw new ApiError(res.status, message, data?.details);
  }
  return data as T;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${config.API_BASE_URL}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...getAuthHeaders(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return parse<T>(res);
}

/** Dépôt d'un fichier (PDF des comptes rendus de GT). */
async function upload<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${config.API_BASE_URL}${path}`, {
    method: 'POST',
    // Surtout PAS de Content-Type ici : le navigateur doit poser lui-même la
    // frontière multipart, qu'on ne peut pas deviner.
    headers: { ...getAuthHeaders() },
    body: form,
  });
  return parse<T>(res);
}

/**
 * Dépôt d'un `FormData` DÉJÀ COMPOSÉ — le fichier et les champs qui
 * l'accompagnent (dossier de destination, description). `upload` ci-dessus ne
 * sait envoyer qu'un fichier nu ; l'étendre aurait obligé tous ses appelants à
 * connaître un paramètre dont ils n'ont pas l'usage.
 */
async function uploadForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${config.API_BASE_URL}${path}`, {
    method: 'POST',
    // Surtout PAS de Content-Type : le navigateur pose la frontière multipart.
    headers: { ...getAuthHeaders() },
    body: form,
  });
  return parse<T>(res);
}

/**
 * Récupère un binaire en portant les en-têtes d'authentification. Un simple
 * <a href> ne les porterait pas : en développement l'identité passe par
 * X-Debug-User, et le lien tomberait en 401.
 */
async function download(path: string): Promise<Blob> {
  const res = await fetch(`${config.API_BASE_URL}${path}`, { headers: { ...getAuthHeaders() } });
  if (!res.ok) {
    const text = await res.text();
    const data = text ? JSON.parse(text) : null;
    throw new ApiError(res.status, data?.error ?? `Erreur ${res.status}`);
  }
  return res.blob();
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  // Un corps sur DELETE : la suppression EN LOT désigne ses cibles par une
  // liste, et les faire tenir dans l'URL les exposerait au journal du serveur
  // comme à l'historique du navigateur.
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  upload,
  uploadForm,
  download,
};
